import * as focusTrap from 'focus-trap';

/**
 * Popup component.
 */
export class Popup {
	constructor( element, openButton ) {
		this.element = element;
		this.openButton = openButton;

		this.closeButton = element.querySelector( '.js-qala-ssc-popup-close' );
		this.title = element.querySelector( '.js-qala-ssc-popup-title' );
		this.loadingElement = element.querySelector( '.js-qala-ssc-popup-loading' );
		this.successElement = element.querySelector( '.js-qala-ssc-popup-success' );
		this.successMsgElement = element.querySelector( '.js-qala-ssc-popup-success-message' );
		this.copiedMsgElement = element.querySelector( '.js-qala-ssc-popup-copied-message' );
		this.errorElement = element.querySelector( '.js-qala-ssc-popup-error' );
		this.errorMsgElement = element.querySelector( '.js-qala-ssc-popup-error-msg' );
		this.cartUrlElement = element.querySelector( '.js-qala-ssc-popup-cart-url' );
		this.copyLinkButton = element.querySelector( '.js-qala-ssc-popup-copy-link' );
		this.trap = focusTrap.createFocusTrap( this.element, {
			escapeDeactivates: true,
			clickOutsideDeactivates: true,
		} );

		this.closeButton.addEventListener( 'click', this.close.bind( this ) );
	}

	/**
	 * Open the popup.
	 *
	 * @return {void}
	 */
	open() {
		this.element.classList.add( 'is-open' );

		this.a11yOpen();
	}

	/**
	 * Close the popup.
	 *
	 * @return {void}
	 */
	close() {
		wp.hooks.doAction( 'qalaSaveShareCart.action.closePopup' );
		this.element.classList.remove( 'is-open' );

		this.a11yClose();
	}

	/**
	 * Disable the close button.
	 *
	 * @return {void}
	 */
	disableClose() {
		this.closeButton.disabled = true;
	}

	/**
	 * Enable the close button.
	 *
	 * @return {void}
	 */
	enableClose() {
		this.closeButton.disabled = false;
	}

	/**
	 * Show loading state.
	 *
	 * @return {void}
	 */
	showLoading() {
		this.loadingElement.classList.add( 'is-visible' );
		this.successElement.classList.remove( 'is-visible' );
		this.errorElement.classList.remove( 'is-visible' );
		this.disableClose();
	}

	/**
	 * Hide loading state.
	 *
	 * @return {void}
	 */
	hideLoading() {
		this.loadingElement.classList.remove( 'is-visible' );
		this.enableClose();
	}

	/**
	 * Show success state.
	 *
	 * @param {Object} cartData The saved cart data.
	 * @return {void}
	 */
	showSuccess( cartData ) {
		this.cartUrlElement.value = cartData?.cartUrl;
		this.successElement.classList.add( 'is-visible' );
		wp.hooks.doAction( 'qalaSaveShareCart.action.showSuccess', cartData );
		this.initCopyLink();
		this.hideLoading();
		this.a11ySuccess();
	}

	/**
	 * Initialize copy link button.
	 *
	 * @return {void}
	 */
	initCopyLink() {
		if ( this.copyLinkButton ) {
			this.copyLinkButton.disabled = false;
			this.copyLinkButton.addEventListener( 'click', () => {
				const text = this.cartUrlElement.value;
				window.navigator.clipboard.writeText( text );

				this.showCopySuccess();
			} );
		}
	}

	/**
	 * Show copy success message.
	 *
	 * @return {void}
	 */
	showCopySuccess() {
		this.copiedMsgElement.classList.add( 'is-visible' );
		this.copiedMsgElement.setAttribute( 'aria-hidden', 'false' );
		this.copiedMsgElement.setAttribute( 'aria-live', 'polite' );
	}

	/**
	 * Show error state.
	 *
	 * @param {string} error The error message.
	 * @return {void}
	 */
	showError( error ) {
		this.errorMsgElement.innerHTML = error;
		this.errorElement.classList.add( 'is-visible' );
		this.hideLoading();
		this.a11yError();
	}

	/**
	 * Accessibility: Set attributes for loading state.
	 *
	 * @return {void}
	 */
	a11yLoading() {
		this.loadingElement.setAttribute( 'aria-hidden', 'false' );
		this.loadingElement.setAttribute( 'aria-live', 'polite' );
	}

	/**
	 * Accessibility: Set attributes for success state.
	 *
	 * @return {void}
	 */
	a11ySuccess() {
		this.loadingElement.setAttribute( 'aria-hidden', 'true' );
		this.successElement.setAttribute( 'aria-hidden', 'false' );
		this.successMsgElement.setAttribute( 'aria-live', 'polite' );
		this.copyLinkButton.focus();
	}

	/**
	 * Accessibility: Set attributes for error state.
	 *
	 * @return {void}
	 */
	a11yError() {
		this.loadingElement.setAttribute( 'aria-hidden', 'true' );
		this.errorElement.setAttribute( 'aria-hidden', 'false' );
		this.errorElement.setAttribute( 'aria-live', 'assertive' );
		this.closeButton.focus();
	}

	/**
	 * Accessibility: Set attributes when opening/closing popup.
	 *
	 * @return {void}
	 */
	a11yOpen() {
		this.trap.activate();
		this.element.setAttribute( 'aria-hidden', 'false' );
		this.openButton.setAttribute( 'aria-expanded', 'true' );
	}

	/**
	 * Accessibility: Set attributes when closing popup.
	 *
	 * @return {void}
	 */
	a11yClose() {
		this.trap.deactivate();
		this.element.setAttribute( 'aria-hidden', 'true' );
		this.openButton.setAttribute( 'aria-expanded', 'false' );
		this.openButton.focus();
	}
}
